// Commission Tracking Data Model
export const commissionModel = {
  // Commission Collection Structure
  collection: 'commissions',
  
  // Default Commission Document Structure
  defaultDocument: {
    id: '', // Auto-generated by Firestore
    bookingId: '', // Reference to booking
    agodaBookingId: '', // Agoda booking ID
    
    // User Information
    userId: '', // User who made the booking
    userName: '', // User's display name
    userRole: 'customer', // customer, momo, girl, admin
    
    // Property Information
    propertyId: '', // Property ID
    propertyName: '', // Property name
    propertyType: '', // hotel, restaurant, attraction
    
    // Booking Information
    bookingDate: '', // ISO string
    checkIn: '', // ISO string
    checkOut: '', // ISO string
    guests: 1, // Number of guests
    rooms: 1, // Number of rooms
    
    // Financial Information
    totalAmount: 0, // Total booking amount
    commissionRate: 0, // Commission rate (decimal)
    commissionAmount: 0, // Calculated commission amount
    currency: 'THB', // Currency code
    
    // Status and Processing
    status: 'pending', // pending, confirmed, paid, cancelled
    paymentStatus: 'unpaid', // unpaid, paid, processing, failed
    paymentDate: null, // ISO string when paid
    
    // Metadata
    createdAt: '', // ISO string
    updatedAt: '', // ISO string
    processedBy: '', // Admin user ID who processed
    
    // Additional Information
    notes: '', // Additional notes
    tags: [], // Tags for categorization
    isActive: true // Whether this commission is active
  },
  
  // Commission Status Options
  statusOptions: [
    { value: 'pending', label: 'รอการยืนยัน', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'confirmed', label: 'ยืนยันแล้ว', color: 'bg-green-100 text-green-800' },
    { value: 'paid', label: 'จ่ายแล้ว', color: 'bg-blue-100 text-blue-800' },
    { value: 'cancelled', label: 'ยกเลิก', color: 'bg-red-100 text-red-800' }
  ],
  
  // Payment Status Options
  paymentStatusOptions: [
    { value: 'unpaid', label: 'ยังไม่จ่าย', color: 'bg-gray-100 text-gray-800' },
    { value: 'processing', label: 'กำลังดำเนินการ', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'paid', label: 'จ่ายแล้ว', color: 'bg-green-100 text-green-800' },
    { value: 'failed', label: 'จ่ายไม่สำเร็จ', color: 'bg-red-100 text-red-800' }
  ],
  
  // Commission Rate by User Role
  commissionRates: {
    customer: 0.05, // 5% for customers
    momo: 0.08, // 8% for momos
    girl: 0.03, // 3% for girls
    admin: 0.10 // 10% for admins
  },
  
  // Validation Rules
  validation: {
    bookingId: {
      required: true,
      message: 'Booking ID is required'
    },
    userId: {
      required: true,
      message: 'User ID is required'
    },
    propertyId: {
      required: true,
      message: 'Property ID is required'
    },
    totalAmount: {
      required: true,
      min: 0,
      message: 'Total amount must be greater than 0'
    },
    commissionRate: {
      required: true,
      min: 0,
      max: 1,
      message: 'Commission rate must be between 0 and 1'
    }
  }
};

// Helper Functions
export const createCommission = (bookingData, userRole = 'customer') => {
  const now = new Date().toISOString();
  
  const commissionRate = commissionModel.commissionRates[userRole] || 0.05;
  const commissionAmount = bookingData.totalAmount * commissionRate;
  
  return {
    ...commissionModel.defaultDocument,
    bookingId: bookingData.bookingId,
    agodaBookingId: bookingData.agodaBookingId,
    userId: bookingData.userId,
    userName: bookingData.userName,
    userRole: userRole,
    propertyId: bookingData.propertyId,
    propertyName: bookingData.propertyName,
    propertyType: bookingData.propertyType,
    bookingDate: bookingData.bookingDate || now,
    checkIn: bookingData.checkIn,
    checkOut: bookingData.checkOut,
    guests: bookingData.guests,
    rooms: bookingData.rooms,
    totalAmount: bookingData.totalAmount,
    commissionRate: commissionRate,
    commissionAmount: commissionAmount,
    currency: bookingData.currency || 'THB',
    createdAt: now,
    updatedAt: now
  };
};

export const validateCommission = (commission) => {
  const errors = {};
  const rules = commissionModel.validation;
  
  // Validate booking ID
  if (!commission.bookingId) {
    errors.bookingId = rules.bookingId.message;
  }
  
  // Validate user ID
  if (!commission.userId) {
    errors.userId = rules.userId.message;
  }
  
  // Validate property ID
  if (!commission.propertyId) {
    errors.propertyId = rules.propertyId.message;
  }
  
  // Validate total amount
  if (!commission.totalAmount || commission.totalAmount < rules.totalAmount.min) {
    errors.totalAmount = rules.totalAmount.message;
  }
  
  // Validate commission rate
  if (commission.commissionRate < rules.commissionRate.min || 
      commission.commissionRate > rules.commissionRate.max) {
    errors.commissionRate = rules.commissionRate.message;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const formatCommissionForDisplay = (commission) => {
  const status = commissionModel.statusOptions.find(s => s.value === commission.status);
  const paymentStatus = commissionModel.paymentStatusOptions.find(s => s.value === commission.paymentStatus);
  
  return {
    ...commission,
    statusDisplay: status ? status.label : 'รอการยืนยัน',
    statusColor: status ? status.color : 'bg-gray-100 text-gray-800',
    paymentStatusDisplay: paymentStatus ? paymentStatus.label : 'ยังไม่จ่าย',
    paymentStatusColor: paymentStatus ? paymentStatus.color : 'bg-gray-100 text-gray-800',
    formattedTotalAmount: `${commission.currency} ${commission.totalAmount.toLocaleString()}`,
    formattedCommissionAmount: `${commission.currency} ${commission.commissionAmount.toLocaleString()}`,
    formattedBookingDate: new Date(commission.bookingDate).toLocaleDateString('th-TH'),
    formattedCheckIn: new Date(commission.checkIn).toLocaleDateString('th-TH'),
    formattedCheckOut: new Date(commission.checkOut).toLocaleDateString('th-TH'),
    commissionPercentage: `${(commission.commissionRate * 100).toFixed(1)}%`
  };
};

// Commission Calculation Helper
export const calculateCommission = (totalAmount, userRole) => {
  const rate = commissionModel.commissionRates[userRole] || 0.05;
  return {
    rate: rate,
    amount: totalAmount * rate,
    percentage: `${(rate * 100).toFixed(1)}%`
  };
};

// Commission Summary Helper
export const calculateCommissionSummary = (commissions) => {
  const summary = {
    totalCommissions: 0,
    totalAmount: 0,
    paidCommissions: 0,
    pendingCommissions: 0,
    cancelledCommissions: 0,
    byStatus: {},
    byPropertyType: {},
    byUserRole: {},
    monthlyBreakdown: {}
  };
  
  commissions.forEach(commission => {
    summary.totalCommissions += 1;
    summary.totalAmount += commission.totalAmount;
    
    // By status
    if (!summary.byStatus[commission.status]) {
      summary.byStatus[commission.status] = { count: 0, amount: 0 };
    }
    summary.byStatus[commission.status].count += 1;
    summary.byStatus[commission.status].amount += commission.commissionAmount;
    
    // By property type
    if (!summary.byPropertyType[commission.propertyType]) {
      summary.byPropertyType[commission.propertyType] = { count: 0, amount: 0 };
    }
    summary.byPropertyType[commission.propertyType].count += 1;
    summary.byPropertyType[commission.propertyType].amount += commission.commissionAmount;
    
    // By user role
    if (!summary.byUserRole[commission.userRole]) {
      summary.byUserRole[commission.userRole] = { count: 0, amount: 0 };
    }
    summary.byUserRole[commission.userRole].count += 1;
    summary.byUserRole[commission.userRole].amount += commission.commissionAmount;
    
    // Monthly breakdown
    const month = new Date(commission.createdAt).toISOString().substring(0, 7); // YYYY-MM
    if (!summary.monthlyBreakdown[month]) {
      summary.monthlyBreakdown[month] = { count: 0, amount: 0 };
    }
    summary.monthlyBreakdown[month].count += 1;
    summary.monthlyBreakdown[month].amount += commission.commissionAmount;
    
    // Status totals
    if (commission.status === 'paid') {
      summary.paidCommissions += commission.commissionAmount;
    } else if (commission.status === 'pending') {
      summary.pendingCommissions += commission.commissionAmount;
    } else if (commission.status === 'cancelled') {
      summary.cancelledCommissions += commission.commissionAmount;
    }
  });
  
  return summary;
};

// Commission Filter Helper
export const filterCommissions = (commissions, filters) => {
  return commissions.filter(commission => {
    // Status filter
    if (filters.status && filters.status !== 'all' && commission.status !== filters.status) {
      return false;
    }
    
    // Payment status filter
    if (filters.paymentStatus && filters.paymentStatus !== 'all' && commission.paymentStatus !== filters.paymentStatus) {
      return false;
    }
    
    // User role filter
    if (filters.userRole && filters.userRole !== 'all' && commission.userRole !== filters.userRole) {
      return false;
    }
    
    // Property type filter
    if (filters.propertyType && filters.propertyType !== 'all' && commission.propertyType !== filters.propertyType) {
      return false;
    }
    
    // Date range filter
    if (filters.startDate && new Date(commission.createdAt) < new Date(filters.startDate)) {
      return false;
    }
    
    if (filters.endDate && new Date(commission.createdAt) > new Date(filters.endDate)) {
      return false;
    }
    
    // Amount range filter
    if (filters.minAmount && commission.commissionAmount < filters.minAmount) {
      return false;
    }
    
    if (filters.maxAmount && commission.commissionAmount > filters.maxAmount) {
      return false;
    }
    
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch = 
        commission.propertyName.toLowerCase().includes(searchLower) ||
        commission.userName.toLowerCase().includes(searchLower) ||
        commission.bookingId.toLowerCase().includes(searchLower) ||
        commission.agodaBookingId.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) {
        return false;
      }
    }
    
    return true;
  });
};

